import { IonInfiniteScroll } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useMemo } from 'react'

import { useSearchUnitActivationsCount, useSearchUnitActivationsInfinite } from '@apps-common/apis/portal/unit-activations'
import { UnitActivationHeader } from '@apps-common/apps/portal/pages/units/pages/activations/components'
import {
  UnitActivationCard,
  UnitActivationCardSkeleton
} from '@apps-common/apps/portal/pages/units/pages/activations/components/activations-infinite'
import { useActivationUpdates } from '@apps-common/apps/portal/pages/units/pages/activations/hooks'
import { InfiniteLoader, NoResultsCard, PullRefresh } from '@apps-common/components/common'
import { LayoutContainer, LayoutPadding } from '@apps-common/components/layout'
import { PageClearFilterButton, PageResultsCount } from '@apps-common/components/page'
import { GRID_CARDS_VARIANTS } from '@apps-common/config/motion-variants'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { useServiceRequestFilterStore, useUnitActivationFilter } from '@apps-common/stores'
import { IActivationServiceRequestModel } from '@apps-common/types/portal/service-request'

const containerId = 'activations-infinite-container'

export const UnitActivationsInfiniteView: React.FC = () => {
  const navigation = useNavigation()
  const { type } = useServiceRequestFilterStore()
  const filter = useUnitActivationFilter()

  const {
    isLoading: countIsLoading,
    isError: countIsError,
    data: countData
  } = useSearchUnitActivationsCount({
    config: {
      enabled: type === 'activations'
    },
    model: filter
  })

  const {
    isLoading: activationsLoading,
    isFetching: activationsFetching,
    isError: activationsError,
    data: activationsData,
    refetch: activationsRefetch,
    fetchNextPage: activationsFetchNextPage
  } = useSearchUnitActivationsInfinite({
    config: {
      enabled: type === 'activations'
    },
    model: filter
  })

  const data = useMemo(
    () =>
      activationsData?.pages?.reduce(
        (acc: IActivationServiceRequestModel[], curr: IActivationServiceRequestModel[]) => [...acc, ...curr],
        []
      ) || [],
    [activationsData]
  )

  const allLoading = activationsLoading || activationsFetching || activationsError || countIsLoading || countIsError

  useActivationUpdates(containerId, activationsLoading, allLoading, data || [])

  const countLoading = countIsLoading || countIsError
  const loading = activationsLoading || activationsError

  return (
    <>
      <UnitActivationHeader navigation={navigation}>
        <div className='flex items-center justify-between gap-[5px]'>
          <PageResultsCount loading={countLoading} count={countData} />
          <PageClearFilterButton filter={filter} filterKeyWordsFunc={filter.getFilteredByKeywords} callback={filter.clear} />
        </div>
      </UnitActivationHeader>
      <LayoutContainer id={containerId} bgClasses='!pt-0'>
        <LayoutPadding topPadding='pt-[30px] sm:pt-[30px]'>
          <PullRefresh loading={loading} callback={() => activationsRefetch()} />
          <AnimatePresence>
            <motion.div
              variants={GRID_CARDS_VARIANTS}
              initial='initial'
              animate='animate'
              className='grid sm:grid-cols-2 gap-[15px] sm:gap-[20px]'
            >
              {loading &&
                Array.from(Array(25).keys()).map((k) => (
                  <UnitActivationCardSkeleton
                    key={`activation-list-skeleton-${k}`}
                    bgClasses='bg-surface border border-border min-w-full sm:min-w-[200px] lg:min-w-[250px]'
                    textClasses='text-text'
                    iconClasses='text-text'
                    bgSecondaryClasses='bg-surface-2'
                    textSecondaryClasses='text-text'
                    bgTertiaryClasses='bg-surface'
                  />
                ))}
              {!loading &&
                data.map((a: IActivationServiceRequestModel) => (
                  <UnitActivationCard
                    key={`activation-list-${a.unitActivationRequestId}`}
                    bgClasses='bg-surface border border-border min-w-full lg:min-w-[250px]'
                    textClasses='text-text'
                    iconClasses='text-text'
                    bgSecondaryClasses='bg-surface-2'
                    textSecondaryClasses='text-tex-muted'
                    bgTertiaryClasses='bg-surface-2'
                    activation={a}
                  />
                ))}
              {!loading && data.length === 0 && (
                <NoResultsCard text='No activations found.' classes='py-4 col-span-full'>
                  No device activations match your current filter settings.
                </NoResultsCard>
              )}
            </motion.div>
          </AnimatePresence>
          <IonInfiniteScroll
            onIonInfinite={async (ev) => {
              await activationsFetchNextPage()
              ev.target.complete()
            }}
          >
            <InfiniteLoader key='unit-activations-list-infinite-loader' loading={activationsFetching} />
          </IonInfiniteScroll>
        </LayoutPadding>
      </LayoutContainer>
    </>
  )
}
